import { Link, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import ServiceMegaMenu from "../mega-menu/ServiceMegaMenu";
import { useAuth } from "../../../contexts/AuthContext";
import RadioPlayer from "../../Dashboard/RadioPlayer";

export default function Navigation({ isSticky }) {
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const { user, profile, signOut } = useAuth();

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navLinkClass = (path) =>
    `py-2 text-sm transition-colors duration-300 whitespace-nowrap ${
      isActivePath(path)
        ? 'text-yellow-500 font-semibold'
        : 'text-white hover:text-yellow-500'
    }`;

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setServicesMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRef]);

  useEffect(() => {
    function handleScroll() {
      if (servicesMenuOpen) setServicesMenuOpen(false);
    }
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [servicesMenuOpen]);

  const toggleServicesMenu = () => setServicesMenuOpen(!servicesMenuOpen);

  return (
    <nav className="relative z-50">
      <div className="flex justify-between items-center w-full gap-4">

        {/* Left — logo + nav links */}
        <div className="flex items-center gap-5 min-w-0">
          <Link to="/" className="flex-shrink-0">
            <img
              className={`transition-all duration-300 ${isSticky ? 'h-11' : 'h-12'}`}
              src="/images/logo.svg"
              alt="logo"
            />
          </Link>

          <ul className="hidden lg:flex items-center gap-5">
            <li>
              <Link to="/about-us" className={navLinkClass('/about-us')}>About Us</Link>
            </li>

            <li ref={menuRef} className="relative">
              <button
                onClick={toggleServicesMenu}
                className={`flex items-center gap-1 ${navLinkClass('/services')}`}
              >
                Services
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-3.5 w-3.5 transition-transform duration-300 flex-shrink-0 ${servicesMenuOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {servicesMenuOpen && <ServiceMegaMenu onClose={() => setServicesMenuOpen(false)} />}
            </li>

            <li>
              <Link to="/portfolio" className={navLinkClass('/portfolio')}>Portfolio</Link>
            </li>

            <li>
              <Link to="/contact-us" className={navLinkClass('/contact-us')}>Contact Us</Link>
            </li>
          </ul>
        </div>

        {/* Right — radio + auth */}
        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          <RadioPlayer />

          <div className="w-px h-5 bg-white/10 flex-shrink-0" />

          {user ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#C9A84C]/20 border border-[#C9A84C]/40 flex items-center justify-center flex-shrink-0">
                <span className="text-[#C9A84C] text-xs font-bold">
                  {(profile?.full_name || user.email || '?')[0].toUpperCase()}
                </span>
              </div>
              <span className="text-gray-400 text-xs max-w-[90px] truncate hidden xl:block">
                {profile?.full_name || user.email}
              </span>
              <Link
                to="/dashboard"
                className="px-3 py-1.5 bg-[#C9A84C] hover:bg-[#A8863C] text-black text-xs font-semibold rounded tracking-wide transition-colors duration-200 whitespace-nowrap"
              >
                Dashboard
              </Link>
              <button
                onClick={signOut}
                className="px-3 py-1.5 border border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]/60 text-xs font-medium rounded tracking-wide transition-colors duration-200 whitespace-nowrap"
              >
                Log Out
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="text-sm text-white/80 hover:text-yellow-400 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Login
              </Link>
              <span className="w-px h-3.5 bg-white/25 flex-shrink-0" />
              <Link
                to="/signup"
                className="text-sm text-white/80 hover:text-yellow-400 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Create Account
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
